import { analyticsEvents, type AnalyticsEventKey } from './analytics.events'

type AnalyticsValue = string | number | boolean | null
export type AnalyticsProps = Record<string, AnalyticsValue | undefined>
export type AnalyticsProvider = 'noop' | 'gtag' | 'gtm'

type AnalyticsWindow = Window & {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
}

type AnalyticsAdapter = {
    track: (event: string, props?: Record<string, AnalyticsValue>) => void
    /** Params that persist onto every later event (GA4 user/config scope, GTM dataLayer). */
    setContext: (props: Record<string, AnalyticsValue>) => void
}

// Mirrors useLogin's AUTH_STORAGE_KEYS.AUTHENTICATION — keep them in step.
const AUTH_STORAGE_KEY = 'u25_authentication'

const noopAdapter: AnalyticsAdapter = {
    track: () => {},
    setContext: () => {}
}

const gtagAdapter: AnalyticsAdapter = {
    track: (event, props) => {
        ;(window as AnalyticsWindow).gtag?.('event', event, props ?? {})
    },
    // GA4 persists a `set` value onto every subsequent event — this is what ties a whole
    // checkout journey back to one BA id.
    setContext: props => {
        ;(window as AnalyticsWindow).gtag?.('set', props)
    }
}

// GTM Data Layer Variables read the LAST value pushed for a key, so a param from one event
// lingers and bleeds onto later events whose tag maps it (e.g. page_path showing up on a cart
// event). Clear the previous event's own keys before each push so every event carries only its
// own params — except context keys, which are meant to persist.
let lastKeys: string[] = []
const gtmAdapter: AnalyticsAdapter = {
    track: (event, props) => {
        const reset = Object.fromEntries(lastKeys.filter(key => !contextKeys.has(key)).map(key => [key, undefined]))
        ;(window as AnalyticsWindow).dataLayer?.push({ ...reset, event, ...(props ?? {}) })
        lastKeys = Object.keys(props ?? {})
    },
    setContext: props => {
        ;(window as AnalyticsWindow).dataLayer?.push(props)
    }
}

const loadGtm = (containerId: string) => {
    const w = window as AnalyticsWindow
    w.dataLayer = w.dataLayer ?? []
    if (document.querySelector(`script[data-gtm-id="${containerId}"]`)) return

    w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })
    const script = document.createElement('script')
    script.async = true
    script.dataset.gtmId = containerId
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`
    document.head.appendChild(script)
}

// Direct GA4, no container. Used when the build is given a measurement id instead of a GTM
// container — the same bootstrap the Oman `purchase` ecommerce event already does for itself.
const loadGtag = (measurementId: string) => {
    const w = window as AnalyticsWindow
    if (typeof w.gtag === 'function') return

    w.dataLayer = w.dataLayer ?? []
    w.gtag = function gtag(...args: unknown[]) {
        w.dataLayer!.push(args)
    }
    w.gtag('js', new Date())
    w.gtag('config', measurementId)

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
    document.head.appendChild(script)
}

export const createAnalyticsAdapter = (provider: AnalyticsProvider): AnalyticsAdapter => {
    if (provider === 'gtag') return gtagAdapter
    if (provider === 'gtm') return gtmAdapter
    return noopAdapter
}

let adapter = createAnalyticsAdapter('noop')
const contextKeys = new Set<string>(['user_id'])

export const setAnalyticsProvider = (provider: AnalyticsProvider) => {
    adapter = createAnalyticsAdapter(provider)
}

const readStoredBaId = (): string => {
    try {
        const stored = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || 'null') as { baId?: string } | null
        return stored?.baId ? String(stored.baId) : ''
    } catch {
        return ''
    }
}

// An explicit provider wins; otherwise infer from whichever id the build was given, then from
// a dataLayer an index.html snippet may already have created. Nothing configured → noop, so a
// local build and CI send nothing at all.
const resolveProvider = (w: AnalyticsWindow): AnalyticsProvider => {
    const declared = import.meta.env.VITE_ANALYTICS_PROVIDER
    if (declared === 'gtm' || declared === 'gtag') return declared
    if (import.meta.env.VITE_GTM_ID) return 'gtm'
    if (import.meta.env.VITE_GA4_ID) return 'gtag'
    return w.dataLayer ? 'gtm' : 'noop'
}

/** Call once from main.tsx, before React mounts. */
export const initAnalytics = () => {
    const w = window as AnalyticsWindow
    const provider = resolveProvider(w)
    if (provider === 'noop') return

    const storedBaId = readStoredBaId()

    if (provider === 'gtm') {
        // Seed user_id BEFORE the container loads: GTM's Google tag fires on "Initialization -
        // All Pages", earlier than App.tsx's identify(), so a returning user's reload would
        // otherwise send that first tag with no user_id.
        w.dataLayer = w.dataLayer ?? []
        if (storedBaId) w.dataLayer.push({ user_id: storedBaId })
        const gtmId = import.meta.env.VITE_GTM_ID
        if (gtmId) loadGtm(gtmId)
    } else {
        const ga4Id = import.meta.env.VITE_GA4_ID
        if (ga4Id) loadGtag(ga4Id)
    }

    setAnalyticsProvider(provider)

    // `market` is the build target and is known here; App.tsx adds the alpha-3 `country` and
    // `language` once the countries API answers. If that call fails, `market` is still the one
    // thing that says which bundle the user was on. `is_guest` is seeded from storage so the
    // first events of a restored session are not mislabelled as guest before React mounts.
    analytics.setContext({ market: __COUNTRY__, is_guest: !storedBaId })
    // gtag queues calls into dataLayer, so this lands even before the script finishes loading.
    if (provider === 'gtag' && storedBaId) analytics.identify(storedBaId)
}

// `undefined` params are dropped rather than sent as nulls, so an absent id never shows up in
// GA4 as a real "(not set)" value on the event.
const clean = (props?: AnalyticsProps) => {
    if (!props) return undefined
    return Object.fromEntries(Object.entries(props).filter(([, value]) => value !== undefined)) as Record<string, AnalyticsValue>
}

export const analytics = {
    track: (event: AnalyticsEventKey, props?: AnalyticsProps) => {
        adapter.track(analyticsEvents[event], clean(props))
    },
    /** Session-wide params — set once, attached to every event that follows. */
    setContext: (props: AnalyticsProps) => {
        const cleaned = clean(props)
        if (!cleaned) return
        for (const key of Object.keys(cleaned)) contextKeys.add(key)
        adapter.setContext(cleaned)
    },
    identify: (userId: string) => {
        adapter.setContext({ user_id: userId })
    },
    reset: () => {
        adapter.setContext({ user_id: null })
    }
}
